// Angular imports
import { DOCUMENT } from '@angular/common';
import {
  Component,
  HostBinding,
  HostListener,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  GuardsCheckEnd,
  NavigationEnd,
  Router
} from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

// 3rd party imports
import themes from 'devextreme/ui/themes';

//Cc-Common imports
import {
  CCMessageBoxIcon,
  MessageBoxComponent,
  PrintableCharsPipe,
  Utils
} from '@therealmanage/ciranet-ui-common';

// Component imports
import { DevToolsComponent } from './dev-tools/dev-tools.component';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Service imports
import { AppInfoService } from './_services/app-info.service';
import { AuthService } from './_services/auth.service';
import { DatadogService } from './_services/datadog.service';
import { EnvironmentConfigService } from './_services/environment-config.service';
import { ScreenService } from './_services/shared-services/screen.service';
import { ServerErrorService } from './_services/shared-services/server-error.service';
import { UserBackgroundService } from './_services/shared-services/user-background.service';
import { UserService } from './_services/data-services/user.service';
import { CustomAnalyticsService } from './_services/shared-services/analytics.service';
import { InactivityService } from './_services/shared-services/inactivity.service';

// Constant imports
import { StorageKeyConstants } from './_constants/storage-key-constants';
import { ThemeColors } from './_constants/theme-colors';

// Model imports
import { MessageBoxInfo } from './_models/message-box-info';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [PrintableCharsPipe],
  standalone: false
})
export class AppComponent implements OnInit {
  @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

  @ViewChild(DevToolsComponent, { static: true })
  devToolsComponent: DevToolsComponent;

  title: string;
  jwtHelper = new JwtHelperService();

  routeComplete = false;
  isAuthenticated = false;

  isOutletOnly: boolean;

  // Inactivity warning modal
  showInactivityWarning = false;
  inactivityTimeoutMinutes = 30;

  @HostBinding('class') get getClass() {
    return Object.keys(this.screenService.sizes)
      .filter((cl) => this.screenService.sizes[cl])
      .join(' ');
  }

  constructor(
    // Adding comment for constructor parameters
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private screenService: ScreenService,
    private appInfo: AppInfoService,
    private userBackgroundService: UserBackgroundService,
    private errorService: ServerErrorService,
    private printableChars: PrintableCharsPipe,
    private datadogService: DatadogService,
    private envConfig: EnvironmentConfigService,
    private userService: UserService,
    private analyticsService: CustomAnalyticsService,
    private inactivityService: InactivityService
  ) {
    this.title = this.appInfo.title;
    this.analyticsService.initializeGtm();
  }

  ngOnInit() {
    // User activity logging
    RxjsUtils.subscribeToRouterEvents(this.router, (event: Event) => {
      if (event instanceof GuardsCheckEnd) {
        this.routeComplete = true;
      } else if (event instanceof NavigationEnd) {
        this.isOutletOnly =
          this.activatedRoute.firstChild?.snapshot.data['outletOnly'];

        this.isAuthenticated = this.authService.isLoggedIn;

        this.logUserActivity(event);

        this.analyticsService.pageView(
          window.location.href,
          document.title,
          event.urlAfterRedirects
        );
      }
    });

    this.isAuthenticated = this.authService.isLoggedIn;

    // On a hard refresh the login-success event does not re-fire (the session is
    // restored from the stored token), so an already-authenticated session must
    // prefetch board access at bootstrap — otherwise _boardAccess stays unloaded
    // and board visibility reads false until the next login.
    if (this.isAuthenticated) {
      this.userService.getBoardAccess().subscribe();
    }

    RxjsUtils.subscribeToSubject(
      this.authService.getSubjectLoginSuccess(),
      (flag: boolean) => {
        this.isAuthenticated = flag;
        if (flag) {
          const userInfo = this.authService.getCurrentUserInfo();
          if (userInfo && userInfo.userId) {
            this.analyticsService.setUserId(userInfo.userId);
          }
          // Warm the board-access cache on login so BoardGuard and board-aware
          // components can resolve visibility without a first-navigation fetch.
          // An empty array (no/revoked board membership) is a valid result.
          this.userService.getBoardAccess().subscribe();
        } else {
          this.analyticsService.clearUserId();
        }
      }
    );

    // Adding a property can change board membership (e.g. a community where the
    // user is a board member becomes accessible), so refresh the board-access
    // cache. clearCache bypasses the 15-min HTTP cache so the new community's
    // grant is picked up without requiring a logout/login.
    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectNewPropertyAdded(),
      () => {
        this.userService.getBoardAccess(true).subscribe();
      }
    );

    // Check if token exists. If yes store decoded token in auth services
    const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
    if (Utils.notNullAndDefined(token)) {
      this.authService.decodedToken = this.jwtHelper.decodeToken(token);
    }

    // Initialize Datadog user session if user is logged in
    this.initializeDatadogUser();

    // Initialize GA4 user_id if user is authenticated
    this.initializeAnalyticsUser();

    // error message
    RxjsUtils.subscribeToSubject(
      this.errorService.getSubjectErrorMessage(),
      (error) => {
        this.messageBox.showWithFixedWidthAndIcon(
          CCMessageBoxIcon.error,
          'Error',
          error,
          320
        );
      }
    );

    // other message
    RxjsUtils.subscribeToSubject(
      this.errorService.getSubjectMessage(),
      (info: MessageBoxInfo) => {
        this.messageBox.showWithFixedWidthAndIcon(
          info.icon,
          info.title,
          info.message,
          320
        );
      }
    );

    // Set application theme
    this.setApplicationTheme();

    // Initialize inactivity monitoring
    this.initializeInactivityMonitoring();
  }

  isLoginUrl() {
    return window.location.href.indexOf('/user/login') !== -1;
  }

  private setApplicationTheme() {
    // Get user preferred theme color from local storage. If not specified use default theme color for the application
    let themeColor = localStorage.getItem(StorageKeyConstants.userThemeKey);
    if (!themeColor) {
      localStorage.setItem(
        StorageKeyConstants.userThemeKey,
        this.envConfig.defaultThemeColor
      );
      themeColor = this.envConfig.defaultThemeColor;
    }

    const theme = ThemeColors.filter((t) => t.name === themeColor);
    if (!!theme && theme.length > 0) {
      const smallDevice = false;
      // NOTE: We need to take a call of changing theme style dynamically for small devices using the same color scheme.
      // Until then we need to keep the commented code below.
      // if (window.screen.width <= environment.smallDeviceMaxWidth) {
      //   smallDevice = true;
      // }
      this.setCustomStyleSheet(theme[0].customStylesheetName);
      if (smallDevice) {
        themes.current(theme[0].materialStyleName);
      } else {
        themes.current(theme[0].compactStyleName);
      }
    }
  }

  private setCustomStyleSheet(styleName: string) {
    const themeLinks: HTMLCollectionOf<HTMLLinkElement> =
      this.document.getElementsByTagName('link');
    const customThemes: HTMLLinkElement[] = [].slice.call(themeLinks);
    customThemes.forEach((t: HTMLLinkElement) => {
      // Remove all existing custom style sheet links
      const dataRel: Attr = t.attributes.getNamedItem('data-rel');
      if (dataRel && dataRel.value === 'cc-theme' && t.rel === 'stylesheet') {
        t.remove();
      }

      // Add the new custom style sheet based on input style name
      if (t.rel === 'cc-theme') {
        const dataTheme: Attr = t.attributes.getNamedItem('data-theme');
        if (dataTheme.value === styleName) {
          /* Create link document */
          const link = document.createElement('link');
          link.type = 'text/css';
          link.rel = 'stylesheet';
          link.setAttribute('data-theme', styleName);
          link.setAttribute('data-rel', 'cc-theme');
          link.href = t.href;
          /* Append link to the tag name */
          document.getElementsByTagName('head')[0].appendChild(link);
        }
      }
    });
  }

  onActivate(): void {
    window.scroll(0, 0);
  }

  logUserActivity(event: NavigationEnd): void {
    this.userBackgroundService.logActivity(
      event.url,
      'Navigation',
      new Date(),
      'success'
    );
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(e: KeyboardEvent) {
    this.handleInputEvent(e);
  }

  @HostListener('window:paste', ['$event'])
  onPaste(e: ClipboardEvent) {
    this.handleInputEvent(e);
  }

  // Keyboard shortcut to open dev tools (Ctrl+Shift+D) - only for IT users
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'd') {
      event.preventDefault();
      // Only allow dev tools for IT users
      if (this.userService.isITUser() && this.devToolsComponent) {
        this.devToolsComponent.devToolsVisible = true;
      }
    }
  }

  handleInputEvent(e: KeyboardEvent | ClipboardEvent): void {
    const el = e.target as HTMLInputElement;
    if (!!el.value && ['text', 'textarea'].indexOf(el.type) !== -1) {
      // Read ignore printable from intputAttr
      let ignoreHandler: any = el.getAttribute('ignore-printable-chars');
      if (!!ignoreHandler) {
        ignoreHandler = ignoreHandler.toLowerCase() === 'true';
      } else {
        ignoreHandler = false;
      }
      if (ignoreHandler === false) {
        // Read newlines configuration from inputAttr
        let allowNewLines: any = el.getAttribute('allow-newlines');
        if (!!allowNewLines) {
          allowNewLines = allowNewLines.toLowerCase() === 'true';
        } else {
          allowNewLines = el.type === 'textarea';
        }

        // Read extended configuration from inputAttr
        let allowExtended: any = el.getAttribute('allow-extended');
        if (!!allowExtended) {
          allowExtended = allowExtended.toLowerCase() === 'true';
        } else {
          allowExtended = false;
        }

        const value = this.printableChars.transform(el.value, {
          allowNewLines,
          allowExtended
        });

        if (el.value !== value) {
          const event = document.createEvent('Event');
          event.initEvent('change', true, true);
          el.dispatchEvent(event);
        }
      }
    }
  }

  initializeDatadogUser(): void {
    if (this.authService.isLoggedIn) {
      const userInfo = this.authService.getCurrentUserInfo();
      if (userInfo) {
        this.datadogService.setUser(userInfo);
      }
    }
  }

  initializeAnalyticsUser(): void {
    // This ensures accurate user tracking for users who refresh or return with existing session
    if (this.authService.isLoggedIn) {
      const userInfo = this.authService.getCurrentUserInfo();
      if (userInfo && userInfo.userId) {
        this.analyticsService.setUserId(userInfo.userId);
      }
    } else {
      this.analyticsService.clearUserId();
    }
  }

  private initializeInactivityMonitoring(): void {
    // Subscribe to inactivity warning - only show if user is authenticated
    RxjsUtils.subscribeToSubject(this.inactivityService.onShowWarning$, () => {
      if (this.authService.isLoggedIn) {
        this.showInactivityWarning = true;
      }
    });

    // Subscribe to force logout to hide modal before redirect
    RxjsUtils.subscribeToSubject(this.inactivityService.onForceLogout$, () => {
      this.showInactivityWarning = false;
    });

    // Start monitoring if user is already authenticated
    if (this.authService.isLoggedIn) {
      this.inactivityService.startMonitoring({
        inactivityTimeout: this.inactivityTimeoutMinutes * 60 * 1000
      });
    }

    // Subscribe to login/logout to start/stop monitoring
    RxjsUtils.subscribeToSubject(
      this.authService.getSubjectLoginSuccess(),
      (isLoggedIn: boolean) => {
        if (isLoggedIn) {
          this.inactivityService.startMonitoring({
            inactivityTimeout: this.inactivityTimeoutMinutes * 60 * 1000
          });
        } else {
          this.inactivityService.stopMonitoring();
          this.showInactivityWarning = false;
        }
      }
    );
  }

  onStaySignedIn(): void {
    this.showInactivityWarning = false;
    this.inactivityService.extendSession();
  }
}
